import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    LinearProgress,
    Menu,
    MenuItem,
    Select,
    Modal,
    TextField,
    Typography,
    Divider,
    Paper,
    styled
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { addToFavoritesDB, calcRefFees, calculateWfs, removeFromFavoritesDB } from "../../utils/helpers";
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import Google from '@mui/icons-material/Google';
import CalculateIcon from '@mui/icons-material/Calculate';
import { CustomTooltip } from "../../components/CustomTooltip";
import { ArrowDownward } from "@mui/icons-material";
import { getUser } from "../../utils/authService";

// Styled component for marketplace badges
const MarketplaceBadge = styled(Box)(({ theme, marketplace }) => ({
    background: marketplace === 'amazon' ? '#ff9900' : '#0071dc',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '20px',
    fontWeight: 'bold',
    display: 'inline-block',
    marginRight: '10px',
    fontSize: '0.8rem'
}));

// Component for the sortable table header
const SortableTableHeader = (props) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let timer;
        if (isLoading) {
            timer = setTimeout(() => setIsLoading(false), 500);
        }
        return () => clearTimeout(timer);
    }, [isLoading]);

    const handleSort = async () => {
        setIsLoading(true);
        const marketplace = props.marketplace;

        let filters, setFilters, sortASC, setSortASC;

        if (marketplace === 'amazon') {
            filters = props.amazonFilters;
            setFilters = props.setAmazonFilters;
            sortASC = props.amazonSortASC;
            setSortASC = props.setAmazonSortASC;
        } else {
            filters = props.walmartFilters;
            setFilters = props.setWalmartFilters;
            sortASC = props.walmartSortASC;
            setSortASC = props.setWalmartSortASC;
        }

        const newSortASC = !filters.sortBy?.[1];

        setTimeout(() => {
            setSortASC(newSortASC);
            setFilters((prevFilters) => ({
                ...prevFilters,
                sortBy: [props.sortKey, newSortASC],
            }));
        }, 0);
    };

    const marketplace = props.marketplace;
    const filters = marketplace === 'amazon' ? props.amazonFilters : props.walmartFilters;
    const sortKey = props.sortKey;

    return (
        <th
            className="sortColumn"
            onClick={handleSort}
            style={{
                cursor: 'pointer',
                background: marketplace === 'amazon'
                    ? 'rgba(255, 153, 0, 0.1)'
                    : 'rgba(0, 113, 220, 0.1)'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                    {props.columnName}
                    {isLoading ? (
                        <CircularProgress size={20} thickness={5} style={{ marginLeft: '8px', color: 'inherit', }} />
                    ) : (
                        filters.sortBy?.[0] === sortKey && (
                            filters.sortBy[1] ? (
                                <ArrowUpwardOutlinedIcon style={{ fontSize: '100%', fontWeight: 'bold', marginLeft: '4px' }} />
                            ) : (
                                <ArrowDownwardOutlinedIcon style={{ fontSize: '100%', fontWeight: 'bold', marginLeft: '4px' }} />
                            )
                        )
                    )}
                </div>
            </div>
        </th>
    );
};

// Processing function to generate a combined data structure from separate Amazon and Walmart data
const processCombinedData = (amazonData, walmartData) => {
    const combinedMap = new Map();

    // Helper function to extract and clean barcodes from product data
    const extractBarcodes = (product) => {
        if (!product.Barcode) return [];
        return product.Barcode.split(',').map(upc => upc.trim());
    };

    // Process Amazon data
    amazonData.forEach(product => {
        const barcodes = extractBarcodes(product);
        barcodes.forEach(barcode => {
            if (!barcode) return;

            if (!combinedMap.has(barcode)) {
                combinedMap.set(barcode, {
                    barcode,
                    amazonData: [product],
                    walmartData: []
                });
            } else {
                const existingEntry = combinedMap.get(barcode);
                existingEntry.amazonData.push(product);
            }
        });
    });

    // Process Walmart data
    walmartData.forEach(product => {
        const barcodes = extractBarcodes(product);
        barcodes.forEach(barcode => {
            if (!barcode) return;

            if (!combinedMap.has(barcode)) {
                combinedMap.set(barcode, {
                    barcode,
                    amazonData: [],
                    walmartData: [product]
                });
            } else {
                const existingEntry = combinedMap.get(barcode);
                existingEntry.walmartData.push(product);
            }
        });
    });

    // Convert map to array
    return Array.from(combinedMap.values());
};

function BothProductsTable({
    amazonData,
    walmartData,
    setWalmartData,
    amazonFilters,
    walmartFilters,
    setAmazonFilters,
    setWalmartFilters,
    amazonSortASC,
    walmartSortASC,
    setAmazonSortASC,
    setWalmartSortASC,
    isProductsLoading,
}) {
    const [page, setPage] = useState(1);
    const [favorites, setFavorites] = useState({});
    const [barcodeColorMap, setBarcodeColorMap] = useState({});
    const [azPricesList, setAzPricesList] = useState({});
    const [wmPricesList, setWmPricesList] = useState({});
    const [combinedData, setCombinedData] = useState([]);
    const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem("bothPageSize")) || 3);

    // Amazon specific state
    const [azPrep, setAzPrep] = useState(localStorage.getItem("costPrep") || 0);
    const [azShpngCost, setAzSpngCost] = useState(localStorage.getItem("shippingCost") || 0);

    // Walmart specific state
    const [wmPrep, setWmPrep] = useState(localStorage.getItem("costPrep") || 0);
    const [shippingCost, setShippingCost] = useState(localStorage.getItem("shippingCost") || 0);

    // Generate combined data whenever Amazon or Walmart data changes
    useEffect(() => {
        const processed = processCombinedData(amazonData, walmartData);
        setCombinedData(processed);
    }, [amazonData, walmartData]);

    function handlePageSizeChange(newSize) {
        setPageSize(newSize);
        setPage(1); // Reset to first page when changing page size
        localStorage.setItem("bothPageSize", newSize);
    }

    // Constants needed for calculations
    const CATEGORY_BSR_COUNT = {
        'Kindle Store': 6756566, 'Video Shorts': 127580, 'Apps & Games': 936100, 'Baby Products': 4172515,
        'Digital Music': 68941656, 'Alexa Skills': 112529, 'Toys & Games': 9204843,
        'Patio, Lawn & Garden': 22608266, 'Books': 102997339, 'Arts, Crafts & Sewing': 16296162,
        'Software': 177161, 'Sports & Outdoors': 41416624, 'Gift Cards': 28409, 'Video Games': 1204647,
        'Handmade Products': 3644844, 'Clothing, Shoes & Jewelry': 275101033, 'Office Products': 12266328,
        'Grocery & Gourmet Food': 3869382, 'Tools & Home Improvement': 37997159, 'Movies & TV': 7740340,
        'Musical Instruments': 2893522, 'Collectibles & Fine Art': 8802597, 'Appliances': 1565048,
        'Pet Supplies': 8652837, 'Cell Phones & Accessories': 26169713, 'Baby': 4172515,
        'Industrial & Scientific': 20388882, 'Everything Else': 8317014, 'CDs & Vinyl': 8366789,
        'Beauty & Personal Care': 14890600, 'Home & Kitchen': 161989558, 'Electronics': 25856893,
        'Automotive': 46255283, 'Magazine Subscriptions': 42578, 'Health & Household': 11049107,
        'Audible Books & Originals': 743380, 'Kitchen & Dining': 37818304, "Amazon Devices": 1894,
        "Computers & Accessories": 14064999, "Camera & Photo Products": 4021127,
        "Collectible Coins": 210648, "Camera & Photo": 4021127
    };

    function handleChangePrepCost(marketplace, cost) {
        if (marketplace === 'amazon') {
            setAzPrep(cost);
        } else {
            setWmPrep(cost);
        }
        localStorage.setItem("costPrep", cost);
    }

    function handleChangeShippingCost(cost) {
        setShippingCost(cost);
        localStorage.setItem("shippingCost", cost);
    }

    // Initialize random colors for barcode display
    useEffect(() => {
        const generateLightColor = () => `rgb(${Math.floor(Math.random() * 128 + 128)},${Math.floor(Math.random() * 128 + 128)},${Math.floor(Math.random() * 128 + 128)})`;

        setBarcodeColorMap((prevBarcodeColorMap) => {
            const newBarcodeColorMap = { ...prevBarcodeColorMap };

            // Process combined data
            combinedData.forEach(item => {
                if (!newBarcodeColorMap[item.barcode]) {
                    newBarcodeColorMap[item.barcode] = generateLightColor();
                }
            });

            return newBarcodeColorMap;
        });
    }, [combinedData]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [amazonFilters, walmartFilters]);

    // Favorites functionality
    const addToFavorites = (product, marketplace) => {
        const id = marketplace === 'amazon' ? product.asin : product.item_id;
        setFavorites(prev => ({
            ...prev,
            [id]: true
        }));
        addToFavoritesDB(product);
    };

    const removeFromFavorites = (product, marketplace) => {
        const id = marketplace === 'amazon' ? product.asin : product.item_id;
        setFavorites(prev => {
            const newFavorites = { ...prev };
            newFavorites[id] = false;
            return newFavorites;
        });
        removeFromFavoritesDB(id);
    };

    // Handle price changes
    const handleAzPriceChange = (asin, price) => {
        setAzPricesList(prev => ({ ...prev, [asin]: price }));
    };

    const handleWmPriceChange = (itemId, price) => {
        setWmPricesList(prev => ({ ...prev, [itemId]: price }));
    };

    // Check filter properties
    const hasPropertyIsFBA = () => {
        if (amazonFilters && !amazonFilters.hasOwnProperty("isFBM")) return true;
        return amazonFilters && amazonFilters.hasOwnProperty("isFBM") && !amazonFilters.isFBM;
    };

    const hasPropertyIsWFS = () => {
        if (walmartFilters && !walmartFilters.hasOwnProperty("isWFS")) return true;
        return walmartFilters && walmartFilters.hasOwnProperty("isWFS") && !walmartFilters.isWFS;
    };

    // Amazon Row Component
    const AmazonRowDisplay = ({ product, index, barcode }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const [localPrice, setLocalPrice] = useState(azPricesList[product.asin] || product.price);
        const [note, setNote] = useState(localStorage.getItem(`note${product.asin}`));
        const [pack, setPack] = useState(1);

        const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        return (
            <tr style={{ height: '15vh', backgroundColor: 'rgba(255, 153, 0, 0.05)' }}>
                <td className="data-row" style={{ width: '16vw' }}>
                    <div style={{ display: "flex" }}>
                        <div className="amazonImage">
                            <MarketplaceBadge marketplace="amazon">AMAZON</MarketplaceBadge>
                            <img
                                alt="product_image"
                                className="hoverImageTable"
                                loading="lazy"
                                src={product?.image || "/assets/noimage.png"}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null;
                                    currentTarget.src = "/assets/noimage.png";
                                }}
                                style={{
                                    borderRadius: "1vw",
                                    height: "4.5vw",
                                    width: "4.5vw",
                                    objectFit: "scale-down",
                                    position: "relative",
                                    zIndex: "5"
                                }}
                                onClick={() => window.open(`https://amazon.com/dp/${product.asin}?th=1&psc=1`)}
                            />
                        </div>

                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", width: "10vw", height: "10vh", padding: "9px", textAlign: "center", margin: "auto", fontSize: "77.5%", }}>
                            {product?.title && product?.title !== null && product?.title.split(" | At")[0].slice(0, 20)}...{product?.title.split(" | At")[0].slice(-25)}
                            {product?.title && product?.title !== null && <CustomTooltip
                                arrow
                                placement="top-end"
                                title={<>
                                    <Typography color="#7e3e1b" style={{ fontWeight: 'bold', fontSize: 12, zIndex: 99999 }}>{product.title.split(" | At")[0]}</Typography>
                                </>}
                            >
                                <InfoIcon style={{ cursor: "default", fontSize: '75%' }} />
                            </CustomTooltip>}
                            <br />
                            {barcode && <>
                                <span
                                    style={{
                                        padding: "0% .8%",
                                        borderRadius: "30px",
                                        color: "black",
                                        backgroundColor: barcodeColorMap[barcode],
                                        position: "absolute",
                                        marginLeft: "-4.1%",
                                        zIndex: "8",
                                    }}>UPC: {barcode.replace("'", "")}</span>
                            </>}
                            {product.source && <span style={{ marginTop: "1.2%", padding: "0% .8%", borderRadius: "30px", color: "black", backgroundColor: 'lightgray', position: "absolute", marginLeft: "-4.1%", zIndex: "8" }}>Source: <a href={product.source} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>{product.source.replaceAll("www.", "").substring(0, 10)}{product.source.replaceAll("www.", "").length >= 10 ? '...' : ''}</a></span>}
                        </span>

                        {product.csv_image && <div className="sourceImage">
                            <p style={{ textAlign: "center", maxWidth: "4.5vw", cursor: "pointer", margin: 'auto', }}>CSV</p>
                            <img
                                alt="product_image"
                                className="hoverImageTable"
                                loading="lazy"
                                src={product.csv_image ? product.csv_image : "/assets/noimage.png"}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null;
                                    currentTarget.src = "/assets/noimage.png";
                                }}
                                style={{
                                    borderRadius: "1vw",
                                    border: "5px solid rgba(126, 62, 27, 0.5)",
                                    height: "4.5vw",
                                    width: "4.5vw",
                                    objectFit: "scale-down",
                                    position: "relative",
                                    zIndex: "5"
                                }}
                            />
                        </div>}
                    </div>
                </td>
                <td id="categoryTt" className="data-row" style={{ width: '7vw', maxWidth: '8vw' }}>
                    {product.category || "N/A"}<br />
                    BSR: #{product.sales_rank}
                    {CATEGORY_BSR_COUNT[product.category] && (() => {
                        const salesRankPercentage = (product.sales_rank / CATEGORY_BSR_COUNT[product.category]) * 100;
                        return (
                            <>
                                <br />
                                <span style={{ fontSize: "83%" }}>
                                    Top {salesRankPercentage < 0.01 ? "<0.01" : salesRankPercentage.toFixed(salesRankPercentage < 0.1 ? 2 : 1)}%
                                </span>
                            </>
                        );
                    })()}
                    <br />
                    <span style={{ fontSize: "70%" }}>
                        30d: #{product.bsr_avg30}
                    </span>
                </td>
                <td id="asinTt" className="data-row" style={{ width: '7vw', maxWidth: '8vw' }}>
                    <a
                        href={`https://www.amazon.com/dp/${product.asin}/`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "underline", color: "#7e3e1b", fontSize: 16 }}
                    >
                        {product.asin}
                    </a>
                    &nbsp;
                    <ContentCopyIcon
                        style={{ cursor: "copy", marginTop: "-.6%", fontSize: "75%" }}
                        onClick={(e) => {
                            navigator.clipboard.writeText(product.asin.split("/")[product.asin.split("/").length - 1]);
                        }}
                    />
                </td>
                <td id="priceTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    <FormControl sx={{ m: 1 }} variant="standard">
                        <InputLabel htmlFor={`az-${index}-price`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Price /unit</InputLabel>
                        <Input
                            id={`az-${index}-price`}
                            size="small"
                            style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                            placeholder={`${product?.price}` || ""}
                            startAdornment={<InputAdornment position="start" style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>$</InputAdornment>}
                            value={localPrice}
                            onChange={(e) => {
                                const newPrice = e.target.value;
                                setLocalPrice(newPrice);
                                product.price = parseFloat(newPrice) || 0;
                            }}
                            onBlur={() => {
                                handleAzPriceChange(product.asin, product.price);
                                if (setAmazonFilters) setAmazonFilters(amazonFilters);
                            }}
                        />
                    </FormControl>

                    <FormControl sx={{ m: 1 }} variant="standard">
                        <InputLabel htmlFor={`az-${index}-pack`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Pack</InputLabel>
                        <Input
                            id={`az-${index}-pack`}
                            size="small"
                            type="number"
                            style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                            startAdornment={<InputAdornment position="start" style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>#</InputAdornment>}
                            value={pack > 1 ? pack : (product?.pack || 1)}
                            onChange={(e) => {
                                const newPack = parseFloat(e.target.value) || 0;
                                setPack(newPack);
                                product.pack = newPack;
                            }}
                        />
                    </FormControl>
                </td>
                <td id="azPriceTt" className="data-row" style={{ width: "4.5vw", maxWidth: "4.5vw", }}>
                    ${product.az_price ? product.az_price.toFixed(2) : "N/A"}
                    {product.used_bb &&
                        <>
                            <br />
                            <span style={{ borderRadius: "30px", backgroundColor: "lightgreen", padding: "4% 10%", fontSize: "80%" }}>BuyBox</span>
                        </>
                    }
                    <br />
                </td>
                <td id="azFeesTt" className="data-row">
                    {product.ref_fee_perc === 0 || product.fba_fee === 0 ?
                        "N/A"
                        :
                        <>
                            Referral Fees: ${(product.ref_fee_perc * product.az_price).toFixed(2)}
                            <br />
                            {hasPropertyIsFBA() ? <>FBA Cost: ${(product.fba_fee).toFixed(2)}</> : (
                                <FormControl sx={{ m: 1, width: "50%", mx: "auto" }} variant="standard">
                                    <InputLabel htmlFor={`${index}-shipping`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Shipping Cost</InputLabel>
                                    <Input
                                        id={`${index}-shipping`}
                                        size="small"
                                        style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                                        placeholder="0"
                                        startAdornment={<InputAdornment position="start"><Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>$</Typography></InputAdornment>}
                                        onChange={(e) => {
                                            setAzSpngCost(parseFloat(e.target.value || 0));
                                            localStorage.setItem("shippingCost", parseFloat(e.target.value || 0));
                                        }}
                                        value={azShpngCost}
                                    />
                                </FormControl>
                            )}
                        </>}
                </td>
                <td id="azOffersTt" className="data-row" style={{ width: '5vw', justifyContent: 'center' }}>
                    {product.total_offers_count}<sub> seller{product.total_offers_count !== 1 && "s"}</sub>
                    {product.az_sells_it &&
                        <>
                            <br />
                            <span style={{ borderRadius: "30px", backgroundColor: "orange", padding: "4% 10%", fontSize: "80%" }}>AMZ</span>
                        </>
                    }
                </td>
                <td id="costTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    <FormControl sx={{ m: 1 }} variant="standard">
                        <InputLabel htmlFor={`az-${index}-cost`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Cost</InputLabel>
                        <Input
                            id={`az-${index}-cost`}
                            type="number"
                            size="small"
                            style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                            value={azPrep}
                            inputProps={{ min: 0, step: 0.25 }}
                            startAdornment={<InputAdornment position="start"><Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>$</Typography></InputAdornment>}
                            onChange={(e) => {
                                handleChangePrepCost('amazon', e.target.value);
                                product.prep = parseFloat(e.target.value);
                            }}
                            onBlur={(e) => {
                                e.target.value === "" && handleChangePrepCost('amazon', 0);
                                if (setAmazonFilters) setAmazonFilters(amazonFilters);
                            }}
                        />
                    </FormControl>
                </td>
                <td id="profitTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    {(() => {
                        const profit = product.az_price - (parseFloat(product.price) * (parseFloat(product.pack) || 1)) - (product.ref_fee_perc * product.az_price) - (hasPropertyIsFBA() ? product.fba_fee : azShpngCost) - parseFloat(azPrep);

                        return (
                            <p style={{ color: profit < 0 ? '#B33B3B' : '#769358' }}>
                                {profit < 0 ? `-$${(profit * -1).toFixed(2)}` : `+$${profit.toFixed(2)}`}
                            </p>
                        );
                    })()}
                </td>
                <td id="roiTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    {(() => {
                        const profit = product.az_price - (parseFloat(product.price) * (parseFloat(product.pack) || 1)) - (product.ref_fee_perc * product.az_price) - (hasPropertyIsFBA() ? product.fba_fee : azShpngCost) - parseFloat(azPrep);
                        const roi = (profit / ((parseFloat(product.price) * (parseFloat(product.pack) || 1)) + parseFloat(azPrep))) * 100;

                        return (
                            <p style={{ color: roi < 0 ? '#B33B3B' : '#769358' }}>
                                {roi.toFixed(2)}%
                            </p>
                        );
                    })()}
                </td>
                <td id="estSalesTt" className="data-row" style={{ width: "7vw", maxWidth: "6vw" }}>
                    {product.keepa_monthly_sold > 0 ? (
                        <>
                            Verified
                            <br />
                            (by <span style={{ borderRadius: "30px", backgroundColor: "orange", padding: "4% 10%", fontSize: "60%" }}>AMZ</span>):<br />{product.keepa_monthly_sold}+
                        </>
                    ) : <><span style={{ fontSize: "75%" }}>Estimated Sales:</span><br />{product.estimate_sales_30_days}</>}
                </td>
                <td id="notesTt" className="data-row" style={{ width: "7vw", maxWidth: "6vw" }}>
                    <TextField
                        id="standard-multiline-flexible"
                        label={<Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>Note</Typography>}
                        multiline
                        maxRows={4}
                        variant="standard"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        onBlur={(e) => localStorage.setItem(`note${product.asin}`, e.target.value)}
                        InputProps={{
                            style: { fontFamily: "RobotoSlab", }
                        }}
                    />
                </td>
                <td id="actionsTt" className="data-row">
                    <IconButton
                        aria-label="more"
                        aria-controls="long-menu"
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={handleClose}
                        PaperProps={{
                            style: {
                                maxHeight: 48 * 4.5,
                            },
                        }}
                    >
                        <MenuItem onClick={() => window.open(`https://www.google.com/search?q=${product.title.split(" | At")[0]}`, "_blank")}>
                            <CustomTooltip title="Search on Google">
                                <Google style={{ cursor: "pointer" }} />
                            </CustomTooltip>
                            <Typography>Search on Google</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            favorites.hasOwnProperty(product.asin) && favorites[product.asin] ? removeFromFavorites(product, 'amazon') : addToFavorites(product, 'amazon');
                            handleClose();
                        }}>
                            <CustomTooltip title={favorites.hasOwnProperty(product.asin) && favorites[product.asin] ? "Remove from Favorites" : "Add to Favorites"}>
                                {favorites.hasOwnProperty(product.asin) && favorites[product.asin] ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            </CustomTooltip>
                            <Typography>{favorites.hasOwnProperty(product.asin) && favorites[product.asin] ? "Remove from Favorites" : "Add to Favorites"}</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => window.open("https://sellercentral.amazon.com/hz/approvalrequest/restrictions/approve?asin=" + product.asin.split("/").pop())}>
                            <CustomTooltip title="Check if Gated">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                            </CustomTooltip>
                            <Typography>Check if Gated</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => window.open(`https://keepa.com/#!product/1-${product.asin.split("/").pop()}`, "_blank")}>
                            <CustomTooltip title="Search on Keepa.com">
                                <img
                                    className="graphicon"
                                    alt="keepa"
                                    src="/assets/keepa.png"
                                    style={{ width: "1.2vw", height: "1.2vw", }}
                                />
                            </CustomTooltip>
                            <Typography>Search on Keepa.com</Typography>
                        </MenuItem>
                    </Menu>
                </td>
            </tr>
        );
    };

    // Walmart Row Component
    const WalmartRowDisplay = ({ product, index, barcode, setWalmartData }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const [localPrice, setLocalPrice] = useState(wmPricesList[product.item_id] || product.price);
        const [localShippingCost, setLocalShippingCost] = useState(shippingCost);
        const [wfsCost, setWfsCost] = useState(calculateWfs(product?.title, product.height, product.width, product.length, product.weight, product.walmart_price, product.category));
        const [note, setNote] = useState(localStorage.getItem(`note${product.item_id}`));
        const [pack, setPack] = useState(1);
        const [loadingResults, setLoadingResults] = useState(false);

        const handleFetchResults = () => {
            if (loadingResults) return;

            setLoadingResults(true);

            product.isLoading = true;

            const requestOptions = {
                method: "GET",
                mode: "cors",
                headers: {
                    Accept: "application/json; charset=utf8",
                    "Content-Type": "application/json; charset=utf8",
                },
            };

            fetch(`https://server.getmarter.com/result_completion_aniseller_walmart/${barcode}/${getUser()}`, requestOptions)
                .then((response) => response.json())
                .then((data) => {
                    setWalmartData((prevData) => prevData.map(item =>
                        item["Barcode"] === barcode ? { ...item, ...data, isLoading: false } : item
                    ));
                })
                .catch((e) => {
                    setWalmartData((prevData) => prevData.map(item =>
                        item["Barcode"] === barcode ? { ...item, isLoading: false } : item
                    ));
                });
        }

        const refFees = React.useRef(calcRefFees(product.category, product.walmart_price, product.sub_category));

        const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        if (product?.results) {
            if (product.results.length >= 1) {
                return <>{product.results.slice(0, 5).map((item, index) => {
                    return <WalmartRowDisplay
                        key={`wm-${product.item_id}-results-${product.Barcode}-${index}`}
                        product={{ ...item, price: product?.price }}
                        setWalmartData={setWalmartData}
                        index={index}
                        barcode={product.Barcode}
                    />
                })}</>
            }
            return (
                <tr style={{ height: '15vh', backgroundColor: 'rgba(0, 113, 220, 0.05)', margin: 'auto', textAlign: "center" }}>
                    <td id="results" className="data-row" style={{ margin: 'auto', textAlign: "center" }} colSpan={13}>
                        {(!loadingResults && !product.isLoading) && (product.title === "" ? `No exact match found using the barcode ${product.Barcode}` : product.results.length < 1 ? "No results found." : `${product.results.length} results found!`)}
                        {product.title === "" && <>
                            {((product?.isLoading !== undefined && product?.isLoading) || loadingResults) ? <>
                                Using our unique technology to find the correct WM match...<LinearProgress color="green" style={{ margin: "1%" }} />
                            </> : <><br />
                                <Button
                                    onClick={() => handleFetchResults()}
                                    disabled={loadingResults}
                                    style={{
                                        padding: "5px 15px",
                                        borderRadius: "50px",
                                        textTransform: 'none',
                                        fontFamily: 'RobotoSlab',
                                        color: "#7e3e1b",
                                        backgroundColor: "rgba(126, 62, 27, 0.2)",
                                        width: "auto",
                                        fontWeight: "700",
                                    }}>
                                    Click here to search for WM product matches using &nbsp;<span className="ai-text">AI</span>&thinsp;&nbsp;
                                    <SearchSharpIcon />
                                </Button>
                            </>}
                        </>}
                    </td>
                </tr>
            );
        }

        return (
            <tr style={{ height: '15vh', backgroundColor: 'rgba(0, 113, 220, 0.05)' }}>
                <td className="data-row" style={{ width: '16vw' }}>
                    <div style={{ display: "flex" }}>
                        <div className="walmartImage">
                            <MarketplaceBadge marketplace="walmart">WALMART</MarketplaceBadge>
                            <img
                                alt="product_image"
                                className="hoverImageTable"
                                loading="lazy"
                                src={product?.image || "/assets/noimage.png"}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null;
                                    currentTarget.src = "/assets/noimage.png";
                                }}
                                style={{
                                    borderRadius: "1vw",
                                    height: "4.5vw",
                                    width: "4.5vw",
                                    objectFit: "scale-down",
                                    position: "relative",
                                    zIndex: "5"
                                }}
                                onClick={() => window.open(`https://walmart.com/ip/${product.item_id}`)}
                            />
                        </div>

                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", width: "10vw", height: "10vh", padding: "9px", textAlign: "center", margin: "auto", fontSize: "77.5%", }}>
                            {product?.title && product?.title !== null && product?.title.split(" | At")[0].slice(0, 20)}...{product?.title.split(" | At")[0].slice(-25)}
                            {product?.title && product?.title !== null && <CustomTooltip
                                arrow
                                placement="top-end"
                                title={<>
                                    <Typography color="#7e3e1b" style={{ fontWeight: 'bold', fontSize: 12, zIndex: 99999 }}>{product.title.split(" | At")[0]}</Typography>
                                </>}
                            >
                                <InfoIcon style={{ cursor: "default", fontSize: '75%' }} />
                            </CustomTooltip>}
                            <br />
                            {barcode && <>
                                <span
                                    style={{
                                        padding: "0% .8%",
                                        borderRadius: "30px",
                                        color: "black",
                                        backgroundColor: barcodeColorMap[barcode],
                                        position: "absolute",
                                        marginLeft: "-4.1%",
                                        zIndex: "8",
                                    }}>UPC: {barcode.replace("'", "")}</span>
                            </>}
                            {product.source && <span style={{ marginTop: "1.2%", padding: "0% .8%", borderRadius: "30px", color: "black", backgroundColor: 'lightgray', position: "absolute", marginLeft: "-4.1%", zIndex: "8" }}>Source: <a href={product.source} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>{product.source.replaceAll("www.", "").substring(0, 10)}{product.source.replaceAll("www.", "").length >= 10 ? '...' : ''}</a></span>}
                        </span>

                        {product.csv_image && <div className="sourceImage">
                            <p style={{ textAlign: "center", maxWidth: "4.5vw", cursor: "pointer", margin: 'auto', }}>CSV</p>
                            <img
                                alt="product_image"
                                className="hoverImageTable"
                                loading="lazy"
                                src={product.csv_image ? product.csv_image : "/assets/noimage.png"}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null;
                                    currentTarget.src = "/assets/noimage.png";
                                }}
                                style={{
                                    borderRadius: "1vw",
                                    border: "5px solid rgba(126, 62, 27, 0.5)",
                                    height: "4.5vw",
                                    width: "4.5vw",
                                    objectFit: "scale-down",
                                    position: "relative",
                                    zIndex: "5"
                                }}
                            />
                        </div>}
                    </div>
                </td>
                <td id="categoryTt" className="data-row" style={{ width: '7vw', maxWidth: '8vw' }}>
                    {product.category || "N/A"}
                </td>
                <td id="itemIdTt" className="data-row" style={{ width: '7vw', maxWidth: '8vw' }}>
                    <a
                        href={`https://walmart.com/ip/${product.item_id}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "underline", color: "#7e3e1b", fontSize: 16 }}
                    >
                        {product.item_id}
                    </a>
                    &nbsp;
                    <ContentCopyIcon
                        style={{ cursor: "copy", marginTop: "-.6%", fontSize: "75%" }}
                        onClick={(e) => {
                            navigator.clipboard.writeText(product.item_id);
                        }}
                    />
                </td>
                <td id="priceTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    <FormControl sx={{ m: 1 }} variant="standard">
                        <InputLabel htmlFor={`wm-${index}-price`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Price /unit</InputLabel>
                        <Input
                            id={`wm-${index}-price`}
                            size="small"
                            style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                            placeholder={`${product?.price}` || ""}
                            startAdornment={
                                <InputAdornment position="start">
                                    <Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>$</Typography>
                                </InputAdornment>
                            }
                            value={localPrice}
                            onChange={(e) => {
                                const newPrice = e.target.value;
                                setLocalPrice(newPrice);
                                product.price = parseFloat(newPrice) || 0;
                            }}
                            onBlur={() => {
                                handleWmPriceChange(product.item_id, product.price);
                                if (setWalmartFilters) setWalmartFilters(walmartFilters);
                            }}
                        />
                    </FormControl>

                    <FormControl sx={{ m: 1 }} variant="standard">
                        <InputLabel htmlFor={`wm-${index}-pack`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>
                            Pack
                        </InputLabel>
                        <Input
                            id={`wm-${index}-pack`}
                            size="small"
                            type="number"
                            style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                            placeholder={pack.toString()}
                            startAdornment={
                                <InputAdornment position="start">
                                    <Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>#</Typography>
                                </InputAdornment>
                            }
                            value={pack}
                            onChange={(e) => {
                                const newPack = parseFloat(e.target.value) || 0;
                                setPack(newPack);
                                product.pack = newPack;
                            }}
                        />
                    </FormControl>
                </td>
                <td id="azPriceTt" className="data-row" style={{ width: "4.5vw", maxWidth: "4.5vw", }}>
                    ${product.walmart_price ? product.walmart_price.toFixed(2) : "N/A"}
                    {product.used_bb &&
                        <>
                            <br />
                            <span style={{ borderRadius: "30px", backgroundColor: "lightgreen", padding: "4% 10%", fontSize: "80%" }}>BuyBox</span>
                        </>
                    }
                    <br />
                </td>
                <td id="azFeesTt" className="data-row">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div>Referral Fees: ${refFees.current.toFixed(2)}</div>
                        {!hasPropertyIsWFS() ? (
                            <FormControl sx={{ m: 1, width: "50%", mx: "auto" }} variant="standard">
                                <InputLabel htmlFor={`${index}-shipping`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Shipping Cost</InputLabel>
                                <Input
                                    id={`${index}-shipping`}
                                    size="small"
                                    style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                                    placeholder="0"
                                    startAdornment={<InputAdornment position="start"><Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>$</Typography></InputAdornment>}
                                    value={localShippingCost}
                                    onChange={(e) => setLocalShippingCost(e.target.value)}
                                    onBlur={(e) => {
                                        handleChangeShippingCost(e.target.value || 0);
                                        product.shpng_cost = e.target.value || 0
                                        if (setWalmartFilters) setWalmartFilters(walmartFilters);
                                    }}
                                />
                            </FormControl>
                        ) : (
                            <>
                                <FormControl sx={{ m: 1, width: "70%", mx: "auto" }} variant="standard">
                                    <InputLabel htmlFor={`${index}-wfs-price`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>WFS Fees</InputLabel>
                                    <Input
                                        id={`${index}-wfs-price`}
                                        size="small"
                                        type="number"
                                        style={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}
                                        placeholder={wfsCost === "N/A" ? "0.00" : wfsCost.toString()}
                                        startAdornment={<InputAdornment position="start"><Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>$</Typography></InputAdornment>}
                                        endAdornment={<InputAdornment position="end"><IconButton onClick={() => window.open("https://marketplace.walmart.com/walmart-fulfillment-services-pricing/")}><CalculateIcon style={{ color: "#7e3e1b", cursor: 'pointer', }} /></IconButton></InputAdornment>}
                                        value={wfsCost}
                                        onChange={(e) => {
                                            setWfsCost(parseFloat(e.target.value))
                                            product.wfsCost = parseFloat(e.target.value)

                                            const costPrice = product.price;
                                            const shippingCost = product.wfsCost;
                                            const profit = product.walmart_price - costPrice - shippingCost - calcRefFees(product.category, product.walmart_price, product.sub_category) - parseFloat(localStorage.getItem("costPrep") || 0);

                                            // Calculate ROI
                                            const investment = (product.price * 1) + parseFloat(localStorage.getItem("costPrep") || 0);
                                            const roi = (profit / investment) * 100;

                                            // Store the calculated values
                                            product.profit = profit;
                                            product.roi = roi;
                                        }}
                                    />
                                </FormControl>
                            </>
                        )}
                    </div>
                </td>
                <td id="azOffersTt" className="data-row" style={{ width: '5vw', justifyContent: 'center' }}>
                    {product.total_offers_count ?? 0}<sub> seller{(product.total_offers_count ?? 0) !== 1 && "s"}</sub>
                </td>
                <td id="costTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    <FormControl sx={{ m: 1 }} variant="standard">
                        <InputLabel htmlFor={`wm-${index}-cost`} style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Cost</InputLabel>
                        <Input
                            id={`wm-${index}-cost`}
                            type="number"
                            size="small"
                            style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                            value={wmPrep}
                            inputProps={{ min: 0, step: 0.25 }}
                            startAdornment={<InputAdornment position="start"><Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>$</Typography></InputAdornment>}
                            onChange={(e) => {
                                handleChangePrepCost('walmart', e.target.value);
                                product.prep = parseFloat(e.target.value);
                            }}
                            onBlur={(e) => {
                                e.target.value === "" && handleChangePrepCost('walmart', 0)
                                if (setWalmartFilters) setWalmartFilters(walmartFilters);
                            }}
                        />
                    </FormControl>
                </td>
                <td id="profitTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    {(() => {
                        if (hasPropertyIsWFS() && wfsCost === "N/A") {
                            product.profit = -1;
                            return "N/A";
                        }
                        const profit = product.walmart_price - ((localPrice ? parseFloat(localPrice) : product.price) * pack) - (hasPropertyIsWFS() ? wfsCost : parseFloat(localShippingCost ?? 0)) - refFees.current - parseFloat(wmPrep);
                        product.profit = profit;

                        return (
                            <p style={{ color: profit < 0 ? '#B33B3B' : '#769358' }}>
                                {profit < 0 ? `-${(profit * -1).toFixed(2)}` : `+${profit.toFixed(2)}`}
                            </p>
                        );
                    })()}
                </td>
                <td id="roiTt" className="data-row" style={{ width: "6vw", maxWidth: "6vw" }}>
                    {(() => {
                        if (hasPropertyIsWFS() && wfsCost === "N/A") {
                            product.roi = -1;
                            return "N/A";
                        }

                        const profit = product.walmart_price - ((localPrice ? parseFloat(localPrice) : product.price) * pack) - (hasPropertyIsWFS() ? wfsCost : parseFloat(localShippingCost ?? 0)) - refFees.current - parseFloat(wmPrep);
                        const roi = (profit / ((product.price * pack) + parseFloat(wmPrep))) * 100;
                        product.roi = roi;

                        return (
                            <p style={{ color: roi < 0 ? '#B33B3B' : '#769358' }}>
                                {roi.toFixed(2)}%
                            </p>
                        );
                    })()}
                </td>
                <td id="estSalesTt" className="data-row" style={{ width: "7vw", maxWidth: "6vw" }}>
                    {product.reviews < 0 ? "N/A" : product.reviews}<br /><span style={{ fontSize: "75%" }}>Reviews</span>
                </td>
                <td id="notesTt" className="data-row" style={{ width: "7vw", maxWidth: "6vw" }}>
                    <TextField
                        id="standard-multiline-flexible"
                        label={<Typography sx={{ color: "#7e3e1b", fontFamily: "RobotoSlab", }}>Note</Typography>}
                        multiline
                        maxRows={4}
                        variant="standard"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        onBlur={(e) => localStorage.setItem(`note${product.item_id}`, e.target.value)}
                        InputProps={{
                            style: { fontFamily: "RobotoSlab", }
                        }}
                    />
                </td>
                <td id="actionsTt" className="data-row">
                    <IconButton
                        aria-label="more"
                        aria-controls="long-menu"
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={handleClose}
                        PaperProps={{
                            style: {
                                maxHeight: 48 * 4.5,
                            },
                        }}
                    >
                        <MenuItem onClick={() => { window.open(`https://www.google.com/search?q=${product.title.split(" | At")[0]}`, "_blank") }}>
                            <CustomTooltip title="Search on Google">
                                <Google />
                            </CustomTooltip>
                            <Typography>Search on Google</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            favorites.hasOwnProperty(product.item_id) && favorites[product.item_id] ? removeFromFavorites(product, 'walmart') : addToFavorites(product, 'walmart');
                            handleClose();
                        }}>
                            <CustomTooltip title={favorites.hasOwnProperty(product.item_id) && favorites[product.item_id] ? "Remove from Favorites" : "Add to Favorites"}>
                                {favorites.hasOwnProperty(product.item_id) && favorites[product.item_id] ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            </CustomTooltip>
                            <Typography>{favorites.hasOwnProperty(product.item_id) && favorites[product.item_id] ? "Remove from Favorites" : "Add to Favorites"}</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => { window.open(`https://app.getmarter.com/?walmartId=${product.item_id}&price=${(localPrice ? parseFloat(localPrice) : product.price)}`, "_blank") }}>
                            <CustomTooltip title="Search on Marter">
                                <SearchSharpIcon />
                            </CustomTooltip>
                            <Typography>Search on Marter</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => { window.open(`https://www.histomart.com/kp?item_id=${product.item_id}`, "_blank") }}>
                            <CustomTooltip title="Search on Histomart">
                                <SearchSharpIcon />
                            </CustomTooltip>
                            <Typography>Search on Histomart</Typography>
                        </MenuItem>
                    </Menu>
                </td>
            </tr>
        );
    };

    // Calculate current page data
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = combinedData.slice(startIndex, endIndex);

    // Main render function
    return (
        <div className="products-table-container" id="anisellerBothProductTable">
            <table className="products-table" style={{ width: '94vw' }}>
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Category<br />BSR</th>
                        <th>ID</th>
                        <SortableTableHeader
                            columnName={<>Price</>}
                            sortKey="price"
                            marketplace="amazon"
                            amazonFilters={amazonFilters}
                            walmartFilters={walmartFilters}
                            setAmazonFilters={setAmazonFilters}
                            setWalmartFilters={setWalmartFilters}
                            amazonSortASC={amazonSortASC}
                            walmartSortASC={walmartSortASC}
                            setAmazonSortASC={setAmazonSortASC}
                            setWalmartSortASC={setWalmartSortASC}
                        />
                        <th>Platform Price</th>
                        <th>Platform Fees</th>
                        <th>Sellers</th>
                        <th>Add. Costs</th>
                        <SortableTableHeader
                            columnName={<>Profit</>}
                            sortKey="profit"
                            marketplace="amazon"
                            amazonFilters={amazonFilters}
                            walmartFilters={walmartFilters}
                            setAmazonFilters={setAmazonFilters}
                            setWalmartFilters={setWalmartFilters}
                            amazonSortASC={amazonSortASC}
                            walmartSortASC={walmartSortASC}
                            setAmazonSortASC={setAmazonSortASC}
                            setWalmartSortASC={setWalmartSortASC}
                        />
                        <SortableTableHeader
                            columnName={<>ROI</>}
                            sortKey="roi"
                            marketplace="amazon"
                            amazonFilters={amazonFilters}
                            walmartFilters={walmartFilters}
                            setAmazonFilters={setAmazonFilters}
                            setWalmartFilters={setWalmartFilters}
                            amazonSortASC={amazonSortASC}
                            walmartSortASC={walmartSortASC}
                            setAmazonSortASC={setAmazonSortASC}
                            setWalmartSortASC={setWalmartSortASC}
                        />
                        <th>Sales/Reviews</th>
                        <th>Note</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {(combinedData.length === 0) && isProductsLoading === false && (
                        <tr>
                            <td colSpan={13} style={{ textAlign: "center" }}>
                                No products found. No scan credit used.
                            </td>
                        </tr>
                    )}

                    {currentPageData.map((item, index) => {
                        const { barcode, amazonData, walmartData } = item;

                        return (
                            <React.Fragment key={barcode}>
                                {/* Barcode header row */}
                                <tr
                                    style={{
                                        background: '#f0f0f0',
                                        border: '1px solid #ddd'
                                    }}
                                >
                                    <td colSpan={13} style={{ padding: '8px 16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span style={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: barcodeColorMap[barcode],
                                                    color: '#000',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    marginRight: '10px'
                                                }}>
                                                    UPC: {barcode}
                                                </span>
                                                <ContentCopyIcon
                                                    style={{ cursor: 'copy', fontSize: '16px', marginRight: '10px' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(barcode);
                                                    }}
                                                />
                                                {amazonData.length > 0 && (
                                                    <MarketplaceBadge marketplace="amazon">
                                                        Amazon ({amazonData.length})
                                                    </MarketplaceBadge>
                                                )}
                                                {walmartData.length > 0 && (
                                                    <MarketplaceBadge marketplace="walmart">
                                                        Walmart ({walmartData.length})
                                                    </MarketplaceBadge>
                                                )}
                                                <ArrowDownward />
                                            </div>
                                        </div>
                                    </td>
                                </tr>

                                {/* Show Amazon and Walmart products when expanded */}
                                <>
                                    {amazonData.map((product, productIndex) => (
                                        <AmazonRowDisplay
                                            key={`az-${product.asin}-${productIndex}`}
                                            product={product}
                                            index={startIndex + productIndex}
                                            barcode={barcode}
                                        />
                                    ))}
                                    {walmartData.map((product, productIndex) => (
                                        <WalmartRowDisplay
                                            key={`wm-${product.item_id}-${productIndex}`}
                                            product={product}
                                            setWalmartData={setWalmartData}
                                            index={startIndex + productIndex}
                                            barcode={barcode}
                                        />
                                    ))}
                                </>

                            </React.Fragment>
                        );
                    })}

                    {/* Loading indicator */}
                    {(page >= combinedData.length / pageSize) && isProductsLoading && (
                        <tr>
                            <td colSpan={13} style={{
                                width: "100%",
                                fontWeight: "bold",
                                border: '2px solid rgba(126, 62, 27, 0.5)',
                                color: "#7e3e1b",
                                backgroundColor: "rgba(126, 62, 27, 0.2)",
                            }}>
                                &emsp;Scanning...
                                <LinearProgress color="#7e3e1b" style={{ margin: "0.5%", padding: "0.1%" }} />
                            </td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={13} style={{ border: "none" }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 15, gap: 20 }}>
                                {/* Page Size Selector */}
                                <FormControl variant="standard" style={{ minWidth: 120 }}>
                                    <InputLabel style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}>Rows per page</InputLabel>
                                    <Select
                                        value={pageSize}
                                        onChange={(e) => handlePageSizeChange(e.target.value)}
                                        style={{ color: "#7e3e1b", fontFamily: "RobotoSlab" }}
                                    >
                                        <MenuItem value={3}>3</MenuItem>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                    </Select>
                                </FormControl>

                                {/* Pagination Controls */}
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flex: 1 }}>
                                    <Button
                                        variant={page === 1 ? "outlined" : "contained"}
                                        onClick={() => { setPage(p => p - 1); window.location.href = '#'; }}
                                        disabled={page === 1}
                                        style={{
                                            textTransform: 'none',
                                            fontFamily: 'RobotoSlab',
                                            color: page === 1 ? '#9e9e9e' : '#FCF8F3',
                                            borderRadius: '20px',
                                            backgroundColor: page === 1 ? '#FCF8F3' : '#7e3e1b',
                                            border: `1px solid ${page === 1 ? '#9e9e9e' : '#FCF8F3'}`,
                                        }}
                                    >
                                        Previous
                                    </Button>

                                    {(() => {
                                        const totalPages = Math.ceil(combinedData.length / pageSize);
                                        const getPageNumbers = () => {
                                            if (totalPages <= 5) {
                                                return Array.from({ length: totalPages }, (_, i) => i + 1);
                                            }

                                            let start = Math.max(1, page - 2);
                                            let end = Math.min(totalPages, start + 4);

                                            if (end - start < 4) {
                                                start = Math.max(1, end - 4);
                                            }

                                            return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                                        };

                                        return getPageNumbers().map(pageNum => (
                                            <Button
                                                key={pageNum}
                                                variant={pageNum === page ? "contained" : "outlined"}
                                                onClick={() => { setPage(pageNum); window.location.href = '#'; }}
                                                style={{
                                                    textTransform: 'none',
                                                    fontFamily: 'RobotoSlab',
                                                    minWidth: '40px',
                                                    color: pageNum === page ? '#FCF8F3' : '#7e3e1b',
                                                    borderRadius: '20px',
                                                    backgroundColor: pageNum === page ? '#7e3e1b' : '#FCF8F3',
                                                    border: `1px solid ${pageNum === page ? '#FCF8F3' : '#7e3e1b'}`,
                                                }}
                                            >
                                                {pageNum}
                                            </Button>
                                        ));
                                    })()}

                                    <Button
                                        variant={((page) * pageSize) >= combinedData.length ? "outlined" : "contained"}
                                        onClick={() => { setPage(p => p + 1); window.location.href = '#'; }}
                                        disabled={((page) * pageSize) >= combinedData.length}
                                        style={{
                                            textTransform: 'none',
                                            fontFamily: 'RobotoSlab',
                                            color: ((page) * pageSize) >= combinedData.length ? '#9e9e9e' : '#FCF8F3',
                                            borderRadius: '20px',
                                            backgroundColor: ((page) * pageSize) >= combinedData.length ? '#FCF8F3' : '#7e3e1b',
                                            border: `1px solid ${((page) * pageSize) >= combinedData.length ? '#9e9e9e' : '#FCF8F3'}`,
                                        }}
                                    >
                                        Next
                                    </Button>
                                </div>

                                {/* Showing X-Y of Z */}
                                <div style={{ minWidth: 120, textAlign: 'right', fontFamily: 'RobotoSlab', color: '#7e3e1b' }}>
                                    Showing {startIndex + 1}-{Math.min(endIndex, combinedData.length)} of {combinedData.length}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

export default BothProductsTable;